import systemNachrichtService from './systemNachrichtService.js';

/**
 * Client für /api/dashboard. Hält die aktuelle Dashboard-Konfiguration im Speicher,
 * damit Widgets nicht jeweils einen GET feuern. Schreibvorgänge sind pro Resource
 * (Layout / Todos / Notes) granular und debounced.
 */
class DashboardService {
    constructor() {
        this._data = null;
        this._loadPromise = null;
        this._writeTimers = new Map();
    }

    async lese(force = false) {
        if (!force && this._data) return this._data;
        if (!this._loadPromise) {
            this._loadPromise = this._fetch();
        }
        try {
            this._data = await this._loadPromise;
        } finally {
            this._loadPromise = null;
        }
        return this._data;
    }

    async _fetch() {
        try {
            const response = await fetch('/neolohn/api/dashboard');
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return await response.json();
        } catch (error) {
            console.log(error);
            systemNachrichtService.zeigeKleineNachricht('Dashboard konnte nicht geladen werden', -1);
            return { Layout: [], Todos: [], Notes: {} };
        }
    }

    getLayout() {
        return this._data?.Layout || [];
    }

    getTodos() {
        return Array.isArray(this._data?.Todos) ? this._data.Todos : [];
    }

    getNote(instanceId) {
        return this._data?.Notes?.[instanceId] || '';
    }

    setLayout(layout) {
        if (!this._data) this._data = { Layout: [], Todos: [], Notes: {} };
        this._data.Layout = layout;
        this._schedule('layout', async () => {
            await this._put('/neolohn/api/dashboard/layout', { Layout: layout });
            systemNachrichtService.zeigeKleineNachricht('Dashboard gespeichert', 1);
        });
    }

    setTodos(todos) {
        if (!this._data) this._data = { Layout: [], Todos: [], Notes: {} };
        this._data.Todos = todos;
        this._schedule('todos', () => this._put('/neolohn/api/dashboard/todos', { Todos: todos }));
    }

    setNote(instanceId, text) {
        if (!this._data) this._data = { Layout: [], Todos: [], Notes: {} };
        if (!this._data.Notes) this._data.Notes = {};
        this._data.Notes[instanceId] = text;
        this._schedule(`note:${instanceId}`, () => this._put(`/neolohn/api/dashboard/notes/${instanceId}`, { Text: text }));
    }

    async deleteNote(instanceId) {
        if (this._data?.Notes) delete this._data.Notes[instanceId];
        // Pending Schreibvorgang für die gleiche Notiz verwerfen, damit Delete nicht überschrieben wird.
        this._cancel(`note:${instanceId}`);
        try {
            await fetch(`/neolohn/api/dashboard/notes/${instanceId}`, { method: 'DELETE' });
        } catch (error) {
            console.log(error);
        }
    }

    _schedule(key, fn, delay = 400) {
        this._cancel(key);
        const timer = setTimeout(async () => {
            this._writeTimers.delete(key);
            try {
                await fn();
            } catch (error) {
                console.log(error);
                systemNachrichtService.zeigeKleineNachricht('Speichern fehlgeschlagen', -1);
            }
        }, delay);
        this._writeTimers.set(key, timer);
    }

    _cancel(key) {
        if (this._writeTimers.has(key)) {
            clearTimeout(this._writeTimers.get(key));
            this._writeTimers.delete(key);
        }
    }

    async _put(url, body) {
        const response = await fetch(url, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
    }
}

const dashboardService = new DashboardService();
export default dashboardService;
